"use client";

import { SafeHydrate } from "@/SafeHydrate";
import * as React from "react";
import { Star, Quote } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const testimonials = [{
  id: 1,
  author: "Jane D.",
  location: "Green Bay, WI",
  quote: "\"Green Haven transformed our home beyond expectations. The team was professional, clean, and finished the remodel ahead of schedule. Highly recommend!\"",
  rating: 5,
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1557890/img/avatar_jane_d.png",
  initials: "JD"
}, {
  id: 2,
  author: "Mike T.",
  location: "Appleton, WI",
  quote: "Reliable, professional, and great attention to detail. My yard has never looked better! They handled some handyman repairs in one go.",
  rating: 5,
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1557890/img/avatar_mike_t.png",
  initials: "MT"
}, {
  id: 3,
  author: "Sarah Jenkins",
  location: "Oshkosh, WI",
  quote: "We hired them for a complete kitchen renovation. The craftsmanship is top-notch, and they were incredibly communicative throughout the entire process.",
  rating: 5,
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1557890/img/avatar_sarah_j.png",
  initials: "SJ"
}, {
  id: 4,
  author: "David Ross",
  location: "Manitowoc, WI",
  quote: "Excellent handyman work. Fixed our deck and replaced some siding. It's hard to find contractors who actually show up on time, but these guys did.",
  rating: 5,
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1557890/img/avatar_david_r.png",
  initials: "DR"
}];
export default function ClientVoices() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="client-voices" ref={ref} className={`dark bg-background text-foreground py-24 relative overflow-hidden transition-opacity duration-1000 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-5%] w-80 h-80 bg-primary/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="mb-12 md:mb-16 max-w-2xl">
          <h2 className="text-primary font-bold tracking-wide uppercase text-sm mb-3">
            Client Voices
          </h2>
          <h3 className="text-3xl md:text-4xl font-bold font-sans text-white mb-4">
            Trusted by Homeowners
          </h3>
          <p className="text-muted-foreground text-lg">
            Don't just take our word for it. Here is what our clients have to
            say about our craftsmanship and service.
          </p>
        </div>

        <Carousel opts={{
        align: "start",
        loop: true
      }} className="w-full">
          <CarouselContent className="-ml-4">
            {testimonials.map((item, i) => <CarouselItem key={item.id} data-index={i} className="pl-4 md:basis-1/2 lg:basis-1/3">
                <div className="h-full p-1">
                  <Card className="h-full bg-card/30 backdrop-blur-md border-white/10 shadow-lg hover:shadow-primary/5 transition-all duration-300 flex flex-col justify-between group">
                    <CardContent className="p-8 flex flex-col h-full gap-6">
                      {}
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, starIndex) => <Star key={starIndex} className={`size-4 ${starIndex < item.rating ? "fill-primary text-primary" : "fill-muted text-muted"}`} />)}
                      </div>

                      {}
                      <div className="relative flex-grow">
                        <Quote className="absolute -top-2 -left-2 size-8 text-primary/20 rotate-180" />
                        <blockquote className="relative z-10 text-lg md:text-xl italic font-serif text-foreground/90 leading-relaxed pt-4">
                          "{item.quote}"
                        </blockquote>
                      </div>

                      {}
                      <div className="flex items-center gap-4 mt-4 pt-6 border-t border-white/5">
                        <SafeHydrate><Avatar className="size-12 border border-primary/20">
                          <AvatarImage src={item.avatar} alt={item.author} className="object-cover" />
                          <AvatarFallback className="bg-primary/10 text-primary font-bold">
                            {item.initials}
                          </AvatarFallback>
                        </Avatar></SafeHydrate>
                        <div>
                          <div className="font-semibold text-white">
                            {item.author}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {item.location}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </CarouselItem>)}
          </CarouselContent>

          {}
          <div className="flex justify-end gap-2 mt-8 pr-4">
            <CarouselPrevious variant="outline" className="static translate-none size-12 border-primary/30 bg-transparent text-primary hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors" />
            <CarouselNext variant="outline" className="static translate-none size-12 border-primary/30 bg-transparent text-primary hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors" />
          </div>
        </Carousel>
      </div>
    </section>;
}